import React, { useState, useEffect } from 'react';
import API from '../../services/api';

const StoreBikeForm = ({ onComplete }) => {
    const [form, setForm] = useState({
        model_name: '',
        version_type: '',
        cc: '',
        color: '',
        chassis_number: '',
        engine_number: '',
        purchase_price: '',
        condition_note: '',
        remarks: ''
    });
    
    const [colors, setColors] = useState([]);
    const [makesList, setMakesList] = useState([]);
    const [modelsList, setModelsList] = useState([]);
    const [powersList, setPowersList] = useState([]);

    const [engineError, setEngineError] = useState('');
    const [chassisError, setChassisError] = useState('');
    const [loading, setLoading] = useState(false);

    // Initial Data Fetching (Colors & Specs API)
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [resColors, resMakes, resModels, resPowers] = await Promise.all([
                    API.get('/v1/colors'),
                    API.get('/v1/specs/makes').catch(() => ({ data: { data: [] } })),
                    API.get('/v1/specs/models').catch(() => ({ data: { data: [] } })),
                    API.get('/v1/specs/powers').catch(() => ({ data: { data: [] } }))
                ]);
                setColors(resColors.data.data || resColors.data || []);
                setMakesList(resMakes.data.data || resMakes.data || []);
                setModelsList(resModels.data.data || resModels.data || []);
                setPowersList(resPowers.data.data || resPowers.data || []);
            } catch (err) {
                console.error("Data load karne mein error aaya:", err);
            }
        };
        loadInitialData();
    }, []);

    // Duplicate Check function
    const checkDuplicate = async (field, value) => {
        const trimmedVal = (value || '').trim();
        if (trimmedVal.length < 3) return;

        try {
            const res = await API.post('/v1/purchase/check-duplicate-stock', { [field]: trimmedVal });
            if (res.data && res.data.exists) {
                if (field === 'engine_number') {
                    setEngineError('Engine number already exists in available stock.');
                } else if (field === 'chassis_number') {
                    setChassisError('Chassis number already exists in available stock.');
                }
            }
        } catch (err) {
            console.error('Duplicate check error:', err);
        }
    };

    const handleInputChange = (field, value) => {
        setForm({ ...form, [field]: value });
        if (field === 'engine_number') setEngineError('');
        if (field === 'chassis_number') setChassisError('');
    };

    const handleSubmit = async () => {
        if (!form.model_name || !form.version_type || !form.cc || !form.chassis_number || !form.engine_number || !form.purchase_price) {
            alert("Make, Version, CC, Chassis #, Engine # aur Purchase Price lazmi hain!");
            return;
        }
        if (engineError || chassisError) {
            alert("Please fix the duplicate stock errors before submitting.");
            return;
        }

        setLoading(true);
        try {
            const res = await API.post('/v1/purchase/direct-store-bike', form);
            if (res.data.success) {
                alert("Done");
                setForm({
                    model_name: '',
                    version_type: '',
                    cc: '',
                    color: '',
                    chassis_number: '',
                    engine_number: '',
                    purchase_price: '',
                    condition_note: '',
                    remarks: ''
                });
                setEngineError('');
                setChassisError('');
                if (onComplete) onComplete();
            }
        } catch (err) {
            alert(err.response?.data?.error || "Error saving bike");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ background: '#fff', padding: '25px', borderRadius: '12px', border: '1px solid #e2e8f0', maxWidth: '800px', margin: '0 auto' }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#1e293b' }}>Direct Store Bike Entry</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                    <label style={styles.label}>Make *</label>
                    <select
                        style={styles.input}
                        value={form.model_name}
                        onChange={e => setForm({ ...form, model_name: e.target.value })}
                    >
                        <option value="">Select Make</option>
                        {makesList.map((m, idx) => (
                            <option key={idx} value={m.name || m}>{m.name || m}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={styles.label}>Version (Model) *</label>
                    <select
                        style={styles.input}
                        value={form.version_type}
                        onChange={e => setForm({ ...form, version_type: e.target.value })}
                    >
                        <option value="">Select Version</option>
                        {modelsList.map((v, idx) => (
                            <option key={idx} value={v.version || v.name || v}>{v.version || v.name || v}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={styles.label}>CC (Power) *</label>
                    <select
                        style={styles.input}
                        value={form.cc}
                        onChange={e => setForm({ ...form, cc: e.target.value })}
                    >
                        <option value="">Select CC</option>
                        {powersList.map((p, idx) => (
                            <option key={idx} value={p.cc || p.name || p}>{p.cc || p.name || p}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={styles.label}>Color *</label>
                    <select 
                        style={styles.input} 
                        value={form.color} 
                        onChange={e => handleInputChange('color', e.target.value)}
                    >
                        <option value="">Select Color</option>
                        {colors.map((c, idx) => (
                            <option key={c.id || idx} value={c.name}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={styles.label}>Chassis Number *</label>
                    <input 
                        placeholder="Chassis Number" 
                        style={chassisError ? styles.errorInput : styles.input} 
                        value={form.chassis_number} 
                        onChange={e => handleInputChange('chassis_number', e.target.value)} 
                        onBlur={() => checkDuplicate('chassis_number', form.chassis_number)}
                    />
                    {chassisError && <div style={styles.errorText}>{chassisError}</div>}
                </div>
                <div>
                    <label style={styles.label}>Engine Number *</label>
                    <input 
                        placeholder="Engine Number" 
                        style={engineError ? styles.errorInput : styles.input} 
                        value={form.engine_number} 
                        onChange={e => handleInputChange('engine_number', e.target.value)} 
                        onBlur={() => checkDuplicate('engine_number', form.engine_number)}
                    />
                    {engineError && <div style={styles.errorText}>{engineError}</div>}
                </div>
                <div>
                    <label style={styles.label}>Purchase Price *</label>
                    <input 
                        placeholder="Purchase Price" 
                        type="number" 
                        style={styles.input} 
                        value={form.purchase_price} 
                        onChange={e => handleInputChange('purchase_price', e.target.value)} 
                    />
                </div>
                <div>
                    <label style={styles.label}>Condition Note</label>
                    <input 
                        placeholder="Condition Note (Optional)" 
                        style={styles.input} 
                        value={form.condition_note} 
                        onChange={e => handleInputChange('condition_note', e.target.value)} 
                    />
                </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
                <label style={styles.label}>Remarks / Notes</label>
                <textarea 
                    placeholder="Optional transaction remarks..." 
                    style={{ ...styles.input, width: '100%', height: '80px', resize: 'vertical' }} 
                    value={form.remarks} 
                    onChange={e => handleInputChange('remarks', e.target.value)} 
                />
            </div>

            <button onClick={handleSubmit} style={styles.saveBtn} disabled={loading}>
                {loading ? 'Saving...' : 'Save Direct Bike'}
            </button>
        </div>
    );
};

const styles = {
    label: { display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '5px' },
    input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', boxSizing: 'border-box', background: '#fff' },
    errorInput: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ef4444', outline: 'none', fontSize: '13px', boxSizing: 'border-box', background: '#fef2f2' },
    errorText: { color: '#dc2626', fontSize: '11px', marginTop: '3px' },
    saveBtn: { width: '100%', padding: '12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }
};

export default StoreBikeForm;